import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { setAnalyticsContextGetter } from '../../utils/analytics/analytics';

/**
 * Context para compartir el estado actual de analytics (module y section)
 * entre componentes sin necesidad de prop drilling
 * 
 * El hook usePageViewTracker establece automáticamente estos valores
 * basados en la ruta actual, y los componentes pueden leerlos o hacer override
 */
const AnalyticsContext = createContext({
  currentModule: null,
  currentSection: null,
  setCurrentModule: () => {},
  setCurrentSection: () => {}
});

export const useAnalyticsContext = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    // Si no hay provider, retornar valores por defecto (no romper componentes)
    return {
      currentModule: null,
      currentSection: null,
      setCurrentModule: () => {},
      setCurrentSection: () => {}
    };
  }
  return context;
};

export const AnalyticsContextProvider = ({ children }) => {
  const [currentModule, setCurrentModule] = useState(null);
  const [currentSection, setCurrentSection] = useState(null);
  
  // Exponer el contexto para que analytics.js pueda leerlo
  const contextRef = useRef({ currentModule, currentSection });
  
  useEffect(() => {
    contextRef.current = { currentModule, currentSection };
  }, [currentModule, currentSection]);
  
  useEffect(() => {
    // Registrar el getter para que analytics.js pueda leer el contexto
    setAnalyticsContextGetter(() => contextRef.current);
    
    return () => {
      // Limpiar al desmontar
      setAnalyticsContextGetter(null);
    };
  }, []);

  const value = {
    currentModule,
    currentSection,
    setCurrentModule,
    setCurrentSection
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

AnalyticsContextProvider.propTypes = {
  children: PropTypes.node.isRequired
};

